import { Button } from "@/lib/components/Button/Button";
import { useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@/lib/components/Dialog";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { TeamPermissions } from "@repo/api/team-roles";
import { IconPlus, IconTrash, IconUserPlus } from "@tabler/icons-react";
import { Input } from "@/lib/components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { TeamRoles } from "@repo/api/team-roles";
import { cn } from "@/lib/utils";
import { type VariantProps } from "class-variance-authority";
import { useCreateInviteMutation } from "@/lib/queries/invite-queries";
import { queryClient } from "@/lib/queries/query-client";
import { teamQueryKeys } from "@/lib/queries/team-queries";
import Toast from "@/lib/components/Toast";
import { z } from "zod";
// Import FieldArrayWithId for typing the props
import { useFormContext, FieldArrayWithId } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/lib/components/Form";
import { createInviteSchema } from "./Teams";

export type InviteFieldArrayActions = {
  fields: FieldArrayWithId<
    z.infer<ReturnType<typeof createInviteSchema>>,
    "emails",
    "id"
  >[];
  append: (value: { value: string }) => void;
  remove: (index: number) => void;
};

type InviteMembersDialogProps = {
  buttonVariant?: VariantProps<typeof Button>["variant"];
  buttonClassName?: string;
} & InviteFieldArrayActions;

export default function InviteMembersDialog({
  buttonVariant = "outline",
  buttonClassName,
  fields,
  append,
  remove,
}: InviteMembersDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const teamMembership = useCurrentTeamMembership();
  const teamExternalId = teamMembership?.teamExternalId;

  const { control, handleSubmit, reset } =
    useFormContext<z.infer<ReturnType<typeof createInviteSchema>>>();

  const { mutate: createInvite } = useCreateInviteMutation({
    teamExternalId: teamExternalId!,
  });

  if (
    !teamMembership ||
    !teamMembership.permissions.includes(TeamPermissions.TEAM_EDIT) ||
    !teamExternalId
  ) {
    return null;
  }

  const handleInviteMembers = async (
    data: z.infer<ReturnType<typeof createInviteSchema>>,
  ) => {
    try {
      const emailsToInvite = data.emails.map((e) => e.value);
      const uniqueEmails = Array.from(new Set(emailsToInvite));

      for (const email of uniqueEmails) {
        await new Promise<void>((resolve, reject) => {
          createInvite(
            { email: email.trim(), role: data.role },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (error) => {
                reject(error);
              },
            },
          );
        });
      }

      await queryClient.invalidateQueries({
        queryKey: teamQueryKeys.getTeamMembers(teamExternalId!),
        type: "all",
      });

      setOpen(false);
      reset({ emails: [{ value: "" }], role: "editor" });
    } catch {
      Toast.error("Failed to invite members.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant={buttonVariant}
          className={cn("flex items-center font-medium", buttonClassName)}
          icon={<IconUserPlus strokeWidth={2.5} />}
        >
          Invite Members
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg">
        <DialogHeader className="w-full">
          <DialogTitle>Invite Members</DialogTitle>
          <DialogDescription>
            Invite new members to your team.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(handleInviteMembers)}
          className="w-full space-y-4"
        >
          <div>
            <FormLabel className="mb-2">Email Addresses</FormLabel>
            <div className="mt-2 flex flex-col gap-3">
              {fields.map((item, index) => (
                <FormField
                  control={control}
                  key={item.id}
                  name={`emails.${index}.value`}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Input placeholder="Enter email address" {...field} />
                        </FormControl>
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          onClick={() => remove(index)}
                          disabled={fields.length <= 1}
                          className="group h-10 w-12"
                        >
                          <IconTrash
                            size={16}
                            className="text-grey-400 transition-colors group-hover:text-black-400"
                          />
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => append({ value: "" })}
              className="mt-3 flex items-center gap-1 text-primary-400 hover:underline [&_svg]:stroke-[url(#icon-gradient)]"
            >
              <IconPlus size={16} stroke={2.5} className="ml-1 inline" />
              <span className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-xs font-semibold text-transparent">
                Add Another
              </span>
            </button>
          </div>
          <FormField
            control={control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="capitalize" variant="primary">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(TeamRoles).map((role) => (
                      <SelectItem
                        key={role}
                        value={role}
                        className="capitalize"
                      >
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter className="mt-8">
            <Button type="submit" disabled={isLoading} isLoading={isLoading}>
              Invite Members
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
